import React, { Component } from 'react';

import { buildPath } from '../utils/route_utils';
import { trackEvent } from '../utils/analytics';
import { observeRise } from '../utils/reveal';
import '../css/ProductBento.css';

/**
 * ProductBento — home page "products by sector" bento.
 *
 * No header: the four full-bleed photo tiles open the section (the odo
 * quadruped line dominant for patrol & inspection, the welding cell for
 * manufacturing, AI parking for facility management and AI video security
 * for security), and a slim full-width bar under the grid carries the
 * "All products & solutions" link. Every tile is a real <a href> so it
 * works without JS, opens in new tabs, and is keyboard reachable; primary
 * clicks go through props.navigate.
 *
 * Props: lang ('zh' | 'en' | 'ja'), navigate(route).
 */

const LANGS = ['zh', 'en', 'ja'];

const COPY = {
  // Accessible name of the section; there is no visible heading.
  label: { zh: '產品', en: 'Products', ja: '製品' },
  all: {
    zh: '所有產品與方案',
    en: 'All products & solutions',
    ja: 'すべての製品とソリューション'
  }
};

// Eyebrows are written in running case; CSS renders them uppercase.
const EXPLORE = { zh: '了解更多', en: 'Explore', ja: '詳しく見る' };

const TILES = [
  {
    key: 'odo',
    route: '/tech/odo',
    eager: true,
    logo: { src: '/assets/odo/logo/nexuni_odo_9_horizontal_white.svg', alt: 'nexuni odo 9' },
    image: {
      webp: '/assets/home/odo_series.webp',
      jpg: '/assets/home/odo_series.jpg',
      mobile: '/assets/home/odo_series_m.webp'
    },
    alt: {
      zh: 'nexuni odo 9 自主巡檢機器狗站在高壓變電站的碎石地上',
      en: 'nexuni odo 9 Autonomous Inspection Quadruped standing on gravel in a high-voltage substation',
      ja: '高圧変電所の砂利の上に立つ nexuni odo 9 自律巡回四足ロボット'
    },
    eyebrow: {
      zh: 'odo 系列 · 自主巡檢',
      en: 'odo series · autonomous inspection',
      ja: 'odo シリーズ · 自律巡回'
    },
    title: {
      zh: '自主巡檢機器狗',
      en: 'Autonomous Inspection Quadruped',
      ja: '自律巡回四足ロボット'
    },
    desc: {
      zh: '排程巡邏、事件即時反應與巡檢讀值，上下樓梯、斜坡、雨天照常。',
      en: 'Scheduled rounds, event-driven response and inspection readings, on stairs, slopes and in the rain.',
      ja: '定期巡回、イベントへの即時対応、点検の読み取り。階段・斜面・雨天でも。'
    },
    family: 'odo 1 · odo 3 · odo 9',
    link: {
      zh: '探索 odo 系列',
      en: 'Explore the odo series',
      ja: 'odo シリーズを見る'
    }
  },
  {
    key: 'weld',
    route: '/tech/beaver',
    image: {
      webp: '/assets/home/weld_series.webp',
      jpg: '/assets/home/weld_series.jpg',
      mobile: '/assets/home/weld_series_m.webp'
    },
    alt: {
      zh: '焊接機械手臂正在焊接鋼件，藍色電弧與火花四濺',
      en: 'Welding robot arm mid-weld, blue arc and sparks on a steel workpiece',
      ja: '鋼材を溶接中の溶接ロボットアーム、青いアークと火花'
    },
    eyebrow: {
      zh: '焊接工作站 · 製造',
      en: 'Welding cell · Manufacturing',
      ja: '溶接セル · 製造'
    },
    title: {
      zh: '智慧焊接機器人',
      en: 'Intelligent Welding Robot',
      ja: 'スマート溶接ロボット'
    },
    desc: {
      zh: '逐件雷射掃描、來料即焊。免治具，也不需專業焊工守在教導器前。',
      en: 'Laser-scans each piece and welds it as it comes. No jigs, no skilled welder at the pendant.',
      ja: '一品ごとにレーザースキャンし、そのまま溶接。治具なし。ティーチペンダントに熟練溶接工が付く必要もなし。'
    },
    link: EXPLORE
  },
  {
    key: 'parking',
    route: '/tech/pms100',
    image: {
      webp: '/assets/home/parking.webp',
      jpg: '/assets/home/parking.jpg',
      mobile: '/assets/home/parking_m.webp'
    },
    alt: {
      zh: '戶外停車場的 Nexuni 停車閘門柱與車牌辨識車道柵欄',
      en: 'Nexuni parking gate kiosks and plate-recognition barrier lane at an outdoor car park',
      ja: '屋外駐車場の Nexuni 駐車ゲート機とナンバー認識レーンの遮断バー'
    },
    eyebrow: {
      zh: 'AI 停車管理 · 設施管理',
      en: 'AI parking · Facility management',
      ja: 'AI 駐車管理 · 施設管理'
    },
    title: {
      zh: 'AI 智慧停車管理',
      en: 'AI Parking Management',
      ja: 'AI 駐車管理'
    },
    desc: {
      zh: '車牌辨識、繳費與導引，讓停車場無人化運作。',
      en: 'Plate recognition, payment and guidance for unattended car parks.',
      ja: 'ナンバー認識・決済・誘導で無人駐車場を運営。'
    },
    link: EXPLORE
  },
  {
    key: 'security',
    route: '/tech/vs100',
    image: { cutout: '/assets/home/ai_security_cut.png' },
    alt: {
      zh: 'AI 影像保全事件畫面，顯示攝影機縮圖與偵測到的人員，下方為邊緣運算主機',
      en: 'AI video security event screen with camera thumbnails and a detected person, edge box below',
      ja: 'カメラのサムネイルと検知した人物を表示する AI 映像セキュリティ画面と、その下のエッジボックス'
    },
    eyebrow: {
      zh: 'AI 影像保全 · 安防',
      en: 'AI video security · Security',
      ja: 'AI 映像セキュリティ · 防犯'
    },
    title: {
      zh: 'AI 智慧影像保全',
      en: 'AI Video Security',
      ja: 'AI 映像セキュリティ'
    },
    desc: {
      zh: '同時看住每一支攝影機，入侵與越線即時告警並附證據。',
      en: 'Every camera watched at once; intrusions and line-crossings flagged instantly, with evidence.',
      ja: '全カメラを同時監視。侵入・ライン越えを証拠つきで即時通知。'
    },
    link: EXPLORE
  }
];

class ProductBento extends Component {
  constructor(props) {
    super(props);
    this.rootRef = React.createRef();
    this.disposeRise = null;
  }

  componentDidMount() {
    this.disposeRise = observeRise(this.rootRef.current);
  }

  componentWillUnmount() {
    if (this.disposeRise) {
      this.disposeRise();
      this.disposeRise = null;
    }
  }

  lang() {
    return LANGS.includes(this.props.lang) ? this.props.lang : 'en';
  }

  /**
   * Route through props.navigate on a plain primary click; leave
   * modified clicks (new tab / window) and missing navigate to the href.
   */
  go(route, e) {
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) return;
    if (typeof this.props.navigate !== 'function') return;
    e.preventDefault();
    this.props.navigate(route);
  }

  onTileClick(tile, e) {
    trackEvent('select_product', {
      location: 'home_bento',
      product_name: tile.title.en,
      product_route: tile.route
    });
    this.go(tile.route, e);
  }

  onAllClick(e) {
    trackEvent('view_all_products', { location: 'home_bento' });
    this.go('/solutions', e);
  }

  renderMedia(tile, lang) {
    const alt = tile.alt[lang] || tile.alt.en;
    const img = tile.image;

    if (img.cutout) {
      return (
        <div className="bento-media">
          <img src={img.cutout} alt={alt} loading="lazy" decoding="async" />
        </div>
      );
    }

    return (
      <div className="bento-media">
        <picture>
          {img.mobile && (
            <source type="image/webp" media="(max-width: 780px)" srcSet={img.mobile} />
          )}
          <source type="image/webp" srcSet={img.webp} />
          <img
            src={img.jpg}
            alt={alt}
            loading={tile.eager ? undefined : 'lazy'}
            decoding="async"
          />
        </picture>
      </div>
    );
  }

  renderTile(tile, lang) {
    const t = (o) => o[lang] || o.en;

    return (
      <a
        key={tile.key}
        className={`bento-tile bento-tile--${tile.key} rise`}
        href={buildPath(lang, tile.route)}
        onClick={(e) => this.onTileClick(tile, e)}
      >
        {this.renderMedia(tile, lang)}
        <span className="bento-scrim" aria-hidden="true"></span>

        <div className="bento-top">
          {tile.logo && (
            <span className="bento-mark">
              <img className="bento-logo" src={tile.logo.src} alt={tile.logo.alt} decoding="async" />
            </span>
          )}
          <span className="bento-eyebrow">{t(tile.eyebrow)}</span>
        </div>

        <div className="bento-copy">
          <h3 className="bento-title">{t(tile.title)}</h3>
          <p className="bento-desc">{t(tile.desc)}</p>
          {tile.family && <p className="bento-family">{tile.family}</p>}
          <span className="bento-cta">
            {t(tile.link)}
            <span className="arrow" aria-hidden="true">→</span>
          </span>
        </div>
      </a>
    );
  }

  render() {
    const lang = this.lang();
    const t = (o) => o[lang] || o.en;

    return (
      <section className="product-bento" ref={this.rootRef} aria-label={t(COPY.label)}>
        <div className="product-bento-grid">
          {TILES.map((tile) => this.renderTile(tile, lang))}
        </div>

      </section>
    );
  }
}

export default ProductBento;
